import { useEffect, useState, useRef } from 'react';
import { useRouter } from 'next/router';
import { getPreferredLocale, debugAllCookies, setPreferredLocale } from '../../utils/localeUtils';
import { locales, defaultLocale } from '../../../i18n';

const LocaleDetector = () => {
  const router = useRouter();
  const [hasChecked, setHasChecked] = useState(false);
  const userSelectedLocale = useRef(null);
  const monitoringInterval = useRef(null);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    // Get the user's selected locale from URL or cookie
    const currentLocale = router.locale || defaultLocale;
    const preferredLocale = getPreferredLocale();
    
    // Store user's selected locale (prioritize URL locale as it represents current selection)
    if (currentLocale !== defaultLocale) {
      userSelectedLocale.current = currentLocale;
      console.log(`🎯 User selected locale detected: ${currentLocale}`);
    } else if (preferredLocale && locales.includes(preferredLocale)) {
      userSelectedLocale.current = preferredLocale;
      console.log(`🍪 User preferred locale from cookie: ${preferredLocale}`);
    }

    // Continuous monitoring function
    const monitorAndOverrideLocale = () => {
      const currentCookieLocale = getPreferredLocale();
      
      // If user has a selected locale and cookie is different, override it
      if (userSelectedLocale.current && 
          currentCookieLocale !== userSelectedLocale.current) {
        
        console.log(`🔄 Overriding locale cookie: ${currentCookieLocale} → ${userSelectedLocale.current}`);
        setPreferredLocale(userSelectedLocale.current);
        
        // If we're not on the correct locale page, redirect
        if (router.locale !== userSelectedLocale.current) {
          console.log(`🚀 Redirecting to correct locale: ${userSelectedLocale.current}`);
          router.push(router.asPath, router.asPath, { locale: userSelectedLocale.current });
        }
      }
    };

    // Initial check
    if (!hasChecked) {
      debugAllCookies();
      console.log(`Current locale: ${currentLocale}`);
      console.log(`Preferred locale from cookie: ${preferredLocale}`);
      
      // If we have a preferred locale and it's different from current locale
      if (preferredLocale && 
          preferredLocale !== currentLocale && 
          locales.includes(preferredLocale)) {
        
        console.log(`Redirecting to: ${preferredLocale}`);
        router.push(router.asPath, router.asPath, { locale: preferredLocale });
      }
      
      setHasChecked(true);
    }

    // Start continuous monitoring every 2 seconds
    monitoringInterval.current = setInterval(monitorAndOverrideLocale, 2000);

    // Cleanup interval on unmount
    return () => {
      if (monitoringInterval.current) {
        clearInterval(monitoringInterval.current);
      }
    };
  }, [router, hasChecked]);

  // Update user selected locale when route changes
  useEffect(() => {
    if (router.locale && router.locale !== defaultLocale) {
      userSelectedLocale.current = router.locale;
      console.log(`🎯 User selected locale updated from route: ${router.locale}`);
    }
  }, [router.locale]);

  return null; // This component doesn't render anything
};

export default LocaleDetector;
